import React, { useEffect, useMemo, useRef, useState } from 'react'
import './Orders.scss'
import { Typography, Segmented, Row, Col, Pagination, Flex } from 'antd'
import OrderCard from './Components/OrderCard';
import { useSelector, useDispatch } from 'react-redux';
import { getOngoingDeals, getClosedDeals } from '../../redux/slices/OrdersSlice';
import { getAgreementEsignStatus } from '../../redux/slices/EsignSlice';
import Loader from '../../components/CustomLoader/Loader';
import { formatCurrency } from '../../helpers/helpers';

const { Title, Text } = Typography;

const ORDERS_PAGE_SIZE = 10;

const orderStatus = [
  {
    label: 'Ongoing Deals',
    value: 'ongoing'
  },
  {
    label: 'Closed Deals',
    value: 'closed'
  },
];

const Orders = () => {
  const dispatch = useDispatch();
  const { ordersData, loading } = useSelector((state) => state.orders);
  const [selectedTab, setSelectedTab] = useState('ongoing');
  const [currentPage, setCurrentPage] = useState(1);
  const syncedAgreementDeals = useRef(new Set());

  const dealList = useMemo(() => ordersData?.deals || [], [ordersData?.deals]);

  useEffect(() => {
    if (selectedTab === "ongoing") {
      dispatch(getOngoingDeals())
    } else {
      dispatch(getClosedDeals())
    }
  }, [dispatch, selectedTab])

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab, ordersData?.deals]);

  useEffect(() => {
    if (selectedTab !== 'ongoing' || loading) return;

    const pending = dealList.filter((deal) => (
      deal?.dealId
      && !deal?.timeline?.executedDate
      && !syncedAgreementDeals.current.has(deal.dealId)
    ));
    if (!pending.length) return;

    pending.forEach((deal) => syncedAgreementDeals.current.add(deal.dealId));
    let cancelled = false;

    (async () => {
      await Promise.all(pending.map((deal) => dispatch(getAgreementEsignStatus({
        type: 'investor',
        dealId: deal.dealId,
      }))));
      if (!cancelled) dispatch(getOngoingDeals());
    })();

    return () => {
      cancelled = true;
    };
  }, [dealList, dispatch, loading, selectedTab]);

  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * ORDERS_PAGE_SIZE;
    return dealList.slice(start, start + ORDERS_PAGE_SIZE);
  }, [dealList, currentPage]);

  return (
    <div className='orders-container'>
      <Title level={3} style={{ marginBottom: 20 }}>Purchased Deals</Title>
      <Segmented
        options={orderStatus}
        value={selectedTab}
        onChange={(value) => setSelectedTab(value)}
        className="custom-order-segmented"
      />
      {loading ? <Loader /> : (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 30 }}>
            <Col xs={12} lg={6}>
              <div className="order-overview-cards">
                <Text style={{ fontSize: 14, color: "#40566D" }}>Total Amount Invested</Text>
                <Title level={5}>{formatCurrency(ordersData?.summary?.totalInvested)}</Title>
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className="order-overview-cards">
                <Text style={{ fontSize: 14, color: "#40566D" }}>Total {selectedTab === "ongoing" ? "Maturity" : "Matured"} Amount</Text>
                <Title level={5}>{formatCurrency(selectedTab === "ongoing" ? (ordersData?.summary?.totalExpectedMaturity ?? ordersData?.summary?.totalExpectedMaturity) : ordersData.summary?.totalMaturity)}</Title>
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className="order-overview-cards">
                <Text style={{ fontSize: 14, color: "#40566D" }}>{selectedTab === "ongoing" ? "Expected" : "Realized"} Gross Returns</Text>
                <Title level={5}>{formatCurrency(selectedTab === "ongoing" ? (ordersData?.summary?.totalExpectedGrossReturns ?? ordersData?.summary?.expectedGrossReturns) : ordersData?.summary?.totalRealizedGrossReturns)}</Title>
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className="order-overview-cards">
                <Text style={{ fontSize: 14, color: "#40566D" }}>{selectedTab === "ongoing" ? "Expected" : "Realized"} Net Returns</Text>
                <Title level={5}>{formatCurrency(selectedTab === "ongoing" ? (ordersData?.summary?.totalExpectedNetReturns ?? ordersData?.summary?.expectedNetReturns) : ordersData?.summary?.totalRealizedNetReturns)}</Title>
              </div>
            </Col>
          </Row>
          {paginatedDeals.map(item => (
            <OrderCard key={item.dealId} order={item} selectedTab={selectedTab} />
          ))}
          {dealList.length > ORDERS_PAGE_SIZE && (
            <Flex justify="center" style={{ marginTop: 24 }}>
              <Pagination
                current={currentPage}
                pageSize={ORDERS_PAGE_SIZE}
                total={dealList.length}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </Flex>
          )}
        </>
      )}
    </div>
  )
}

export default Orders
